import { defineRecipe } from '@chakra-ui/react'
import * as controls from '@src/theme/next/controls.ts'

// Recipe overrides, keyed like src/theme/recipes (button: defineRecipe({...}), …).
// Overrides deep-merge into the production recipe, so only the keys set here change.

// Tracking tightens as size grows, like the LP: -0.01em at body sizes to -0.04em on display.
const tracking = {
  body: '-0.01em',
  title: '-0.02em',
  section: '-0.03em',
  display: '-0.04em',
}

// Flat fills: drops the production sheen (_after gradient + inset line), text shadow and drop shadow.
const flat = {
  boxShadow: 'none',
  textShadow: 'none',
  _after: { display: 'none' },
}

// Hover for line and ghost buttons: a faint neutral wash, so it reads on translucent surfaces.
const wash = { _light: 'blackAlpha.50', _dark: 'whiteAlpha.100' }

// One step brighter in dark, one step deeper in light.
const solidHover = { _light: 'colorPalette.700', _dark: 'colorPalette.500' }

export const recipes = {
  // Medium-weight headlines (the LP's are 500); small headings keep 600 for card titles.
  heading: defineRecipe({
    base: {
      fontWeight: 'medium',
      letterSpacing: tracking.title,
    },
    variants: {
      size: {
        xs: { fontWeight: 'semibold', letterSpacing: tracking.body },
        sm: { fontWeight: 'semibold', letterSpacing: tracking.body },
        md: { fontWeight: 'semibold', letterSpacing: tracking.body },
        lg: { letterSpacing: tracking.title },
        xl: { letterSpacing: tracking.title },
        '2xl': { letterSpacing: tracking.section },
        '3xl': { letterSpacing: tracking.section },
        '4xl': { letterSpacing: tracking.display },
        '5xl': { letterSpacing: tracking.display },
        '6xl': { letterSpacing: tracking.display },
        '7xl': { letterSpacing: tracking.display },
        '8xl': { letterSpacing: tracking.display },
      },
    },
  }),

  // The mockups' .w-btn: flat fill, near-black text on orange, 600 weight, ~9px corners at 44px.
  // Line and ghost buttons stay medium weight with a neutral wash on hover.
  button: defineRecipe({
    variants: {
      size: {
        '2xs': { borderRadius: 'md' },
        xs: { borderRadius: 'md' },
        sm: { borderRadius: 'lg' },
        md: { borderRadius: 'lg' },
        lg: { borderRadius: 'lg' },
        xl: { borderRadius: 'xl' },
        '2xl': { borderRadius: 'xl' },
      },
      variant: {
        cta: {
          ...flat,
          bgColor: 'orange.solid',
          color: 'orange.contrast',
          _hover: { bg: { _light: 'orange.700', _dark: 'orange.300' } },
          _expanded: { bg: { _light: 'orange.700', _dark: 'orange.300' } },
          _active: {
            bg: { _light: 'orange.800 !important', _dark: 'orange.500 !important' },
            boxShadow: 'none !important',
          },
        },
        solid: {
          ...flat,
          color: 'colorPalette.contrast',
          fontWeight: 'semibold',
          _light: { bg: 'colorPalette.solid' },
          _dark: { bg: 'colorPalette.solid' },
          _hover: { bg: solidHover },
          _expanded: { bg: solidHover },
        },
        outline: {
          _hover: { bg: wash },
          _expanded: { bg: wash },
        },
        outlineGhost: {
          _hover: { bg: wash },
          _expanded: { bg: wash },
        },
        outlineSolid: {
          _after: { display: 'none' },
          _hover: { ...flat, bg: 'colorPalette.solid', color: 'colorPalette.contrast' },
          _active: { ...flat, bg: solidHover, color: 'colorPalette.contrast' },
          _expanded: { ...flat, bg: 'colorPalette.solid', color: 'colorPalette.contrast' },
        },
        ghost: {
          _hover: { bg: wash },
          _expanded: { bg: wash },
        },
      },
    },
  }),

  // Recessed wells with hairline edges (see ./controls.ts).
  input: defineRecipe({
    base: controls.text,
    variants: {
      size: controls.radii,
      variant: controls.variants,
    },
  }),

  textarea: defineRecipe({
    base: controls.text,
    variants: {
      variant: {
        subtle: controls.variants.subtle,
        outline: controls.variants.outline,
        flushed: controls.variants.flushed,
      },
    },
  }),

  // Fully rounded pills with a tinted fill and hairline ring (./controls.ts). Labels drop the
  // production uppercase, like the label text styles.
  badge: defineRecipe({
    base: { borderRadius: 'full', px: '2' },
    variants: {
      variant: {
        subtle: controls.tint,
        surface: {
          ...controls.tint,
          shadowColor: { _light: 'colorPalette.emphasized', _dark: 'colorPalette.400/45' },
        },
        count: { borderTop: 'none', boxShadow: 'none', textShadow: 'none' },
        countGeneric: { textShadow: 'none' },
        label: { fontWeight: 'medium', letterSpacing: '0', textTransform: 'none' },
        title: { fontWeight: 'medium', letterSpacing: '0', textTransform: 'none' },
      },
      size: {
        xs: { px: '1.5' },
        md: { px: '2.5' },
        lg: { px: '3' },
      },
    },
  }),
}
